/* Theme custom file */
import { Component } from '@theme/component';

/**
 * Sticky category bar for the collection grid (B2C-661).
 *
 * Shows the category pills (and, on desktop, the filter trigger) once the
 * in-flow filters bar has scrolled out of the viewport and the user scrolls
 * back up; hides again on scroll down, at the top of the page, and as soon
 * as the original bar is back on screen — the ticket's acceptance criteria.
 * The scroll-up reveal of the header stays the header's own business — this
 * bar just slides below it when it shows up (see the section's CSS).
 *
 * Nothing is read off the header's `data-sticky-state`: that attribute's
 * meaning changes with the merchant's `enable_sticky_header` setting, so
 * consuming it in JS would let a theme-editor change break this bar silently.
 * The only thing borrowed from the header is `--header-height`, a public
 * custom property the theme keeps up to date.
 *
 * Publishes its own height as `--collection-sticky-bar-height` on <body> so
 * the sticky mobile actions can stack below it, and so a scroll-to-anchor
 * lands clear of it.
 *
 * Attributes:
 *   anchor-id  id of the in-flow filters bar this bar stands in for.
 *
 * @extends {Component}
 */
class CollectionStickyBar extends Component {
  /** Sub-pixel and momentum jitter would otherwise flip the bar mid-gesture. */
  static DIRECTION_THRESHOLD = 4;

  #ticking = false;
  #lastScrollTop = 0;
  #scrollingUp = false;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry?.borderBoxSize[0]) return;
    this.#height = Math.round(entry.borderBoxSize[0].blockSize);
    this.#publishHeight();
  });

  /**
   * A facets re-render can morph the `visible` attribute away without ever
   * disconnecting the element — seen right after landing on a category link
   * with a restored scroll position. Watching our own attribute lets the bar
   * re-assert itself immediately instead of staying hidden until the next
   * scroll. No loop: our own toggle re-fires this, but #update then finds the
   * DOM already in the computed state and returns without touching it.
   */
  #attributeObserver = new MutationObserver(() => this.#onScroll());

  #height = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#resizeObserver.observe(this);
    this.#attributeObserver.observe(this, { attributes: true, attributeFilter: ['visible'] });
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#attributeObserver.disconnect();
    document.body.style.removeProperty('--collection-sticky-bar-height');
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  /**
   * Resolved on every use rather than cached at connect: applying a filter
   * re-renders the whole section, so a connect-time reference would go stale.
   */
  get #anchor() {
    return document.getElementById(this.getAttribute('anchor-id') ?? '');
  }

  /** Zero while hidden, so anything stacking below it closes the gap. */
  #publishHeight() {
    document.body.style.setProperty(
      '--collection-sticky-bar-height',
      `${this.hasAttribute('visible') ? this.#height : 0}px`
    );
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  };

  #update = () => {
    // The filter drawer locks scroll by fixing <body> (dialog.js), which
    // zeroes window.scrollY for as long as it stays open. Reacting to that
    // fake zero would hide the bar behind the drawer and slide it back in on
    // close — a visible flash. Freeze instead; the close-time scroll
    // restoration fires a scroll event that resumes normal tracking.
    if (document.body.style.position === 'fixed') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const delta = scrollTop - this.#lastScrollTop;

    if (Math.abs(delta) > CollectionStickyBar.DIRECTION_THRESHOLD) {
      this.#scrollingUp = delta < 0;
      this.#lastScrollTop = scrollTop;
    }

    const anchor = this.#anchor;
    // `bottom < 0` covers both halves of the anchor rule at once: the bar is
    // only eligible once the original has scrolled past, and it stands down
    // again the moment the original re-enters the viewport.
    const anchorPassed = anchor ? anchor.getBoundingClientRect().bottom < 0 : false;
    const visible = anchorPassed && this.#scrollingUp && scrollTop > 0;

    // The DOM attribute is the change guard rather than a private flag: a
    // facets re-render can morph this element's attributes away without ever
    // disconnecting it (seen on landing scrolled mid-page after tapping a
    // category), and a stale private flag would then swallow every toggle
    // until the user scrolled back to the very top.
    if (visible === this.hasAttribute('visible')) return;
    this.toggleAttribute('visible', visible);
    this.#publishHeight();
  };
}

if (!customElements.get('collection-sticky-bar')) {
  customElements.define('collection-sticky-bar', CollectionStickyBar);
}
